import Logo from "../../assets/image/logo.png";
import { useState, useEffect } from "react";
import {
    Button, TextField, Box, Typography, Alert, InputAdornment, IconButton
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../Context/authProvider";
import { getDefaultAdminRoute, resolveUserPermissions } from "../../utils/resolveUserPermissions";
import { fetchPermissionsRegistry, getCachedPermissionsRegistry } from "../../services/permissionsService";
import { delay } from "../../utils/formatters";
import AuthLayout from "./AuthLayout";

function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showOverlay, setShowOverlay] = useState(false);
    const [loginSuccess, setLoginSuccess] = useState(false);
    const [loginError, setLoginError] = useState(false);

    const { login, logout } = useAuth();
    const navigate = useNavigate();

    // Auto logout when landing on the login page (prevents session reuse via back button)
    useEffect(() => {
        logout();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleLogin = async (e) => {
        e.preventDefault();
        setError("");

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email || !password) {
            setError("Compila tutti i campi richiesti.");
            return;
        }
        if (!emailRegex.test(email)) {
            setError("Inserisci un formato email valido.");
            return;
        }

        setLoading(true);
        const result = await login({ email, password });

        if (result?.success) {
            setLoading(false);
            setShowOverlay(true);
            setLoginError(false);

            // Simula un breve caricamento nell'overlay per l'effetto WOW
            await delay(1000);
            setLoginSuccess(true);

            // Dopo un altro secondo naviga alla dashboard
            await delay(1500);
            const normalizedEmail = email.trim().toLowerCase();
            let registry = getCachedPermissionsRegistry();
            try {
              registry = await fetchPermissionsRegistry({ forceRefresh: true });
            } catch (permissionsError) {
              setShowOverlay(false);
              setLoginSuccess(false);
              setError(permissionsError instanceof Error ? permissionsError.message : "Sessione non autenticata.");
              return;
            }
            const resolvedFlags = resolveUserPermissions(
              normalizedEmail,
              registry ?? getCachedPermissionsRegistry() ?? {}
            ).featureFlags;
            const route = getDefaultAdminRoute(resolvedFlags);
            navigate(route ?? "/");
        } else {
            setLoading(false);
            setShowOverlay(true);
            setLoginSuccess(false);
            setLoginError(false); // Reset prima di iniziare il loader dell'overlay

            // Mostra il loader per un secondo prima dell'errore
            await delay(1000);
            setLoginError(true);
            setError(result?.message ?? "Email o password errati. Riprova.");

            // Chiudi l'overlay dopo 2.5 secondi per permettere di riprovare
            await delay(2500);
            setShowOverlay(false);

            // Reset stati dopo la chiusura
            await delay(500);
            setLoginError(false);
        }
    }

    return (
        <AuthLayout
            title="Wow - Accesso Admin"
            showOverlay={showOverlay}
            success={loginSuccess}
            error={loginError}
            loadingMessage="Autenticazione in corso..."
            successTitle="Benvenuto!"
            successMessage="Reindirizzamento alla dashboard..."
            errorTitle="Accesso Negato"
            errorMessage="Credenziali non valide. Riprova."
        >
            <img src={Logo} alt="Logo" height="80px" style={{ objectFit: "contain" }} />

            <Box sx={{ textAlign: "center", width: "100%" }}>
                <Typography variant="h5" component="h1" sx={{ color: "#0b3d87", fontWeight: "bold", mb: 0.5 }}>
                    Benvenuto!
                </Typography>
                <Typography variant="body2" sx={{ color: "#0b3d87", fontSize: "1.2rem" }}>
                    Accedi al pannello <Box component="span" sx={{ color: "#e00036", fontWeight: "bold", fontSize: "1.2rem" }}>WOW</Box>
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ width: "100%" }} aria-live="assertive">
                    {error}
                </Alert>
            )}

            <Box component="form" onSubmit={handleLogin} sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 3 }}>
                <TextField
                    fullWidth
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    variant="outlined"
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#e3ebf3',
                            borderRadius: '4px',
                            '& fieldset': { border: 'none' },
                        },
                        '& .MuiInputBase-input': {
                            padding: '12px 14px',
                            fontSize: '14px',
                            color: '#0b3d87',
                            fontWeight: 500,
                            '&::placeholder': {
                                color: '#0b3d87',
                                opacity: 1,
                            },
                            '&:-webkit-autofill': {
                                WebkitBoxShadow: '0 0 0 100px #e3ebf3 inset !important',
                                WebkitTextFillColor: '#0b3d87 !important',
                            },
                        }
                    }}
                />

                <TextField
                    fullWidth
                    placeholder="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    variant="outlined"
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#e3ebf3',
                            borderRadius: '4px',
                            '& fieldset': { border: 'none' },
                        },
                        '& .MuiInputBase-input': {
                            padding: '12px 14px',
                            fontSize: '14px',
                            color: '#0b3d87',
                            fontWeight: 500,
                            '&::placeholder': {
                                color: '#0b3d87',
                                opacity: 1,
                            },
                            '&:-webkit-autofill': {
                                WebkitBoxShadow: '0 0 0 100px #e3ebf3 inset !important',
                                WebkitTextFillColor: '#0b3d87 !important',
                            },
                        }
                    }}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                                        onClick={() => setShowPassword(!showPassword)}
                                        onMouseDown={(e) => e.preventDefault()}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }
                    }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%', mt: -1 }}>
                    <Typography
                        variant="caption"
                        onClick={() => navigate("/recupero-password")}
                        sx={{ 
                            color: '#0b3d87', 
                            cursor: 'pointer', 
                            fontWeight: 600, 
                            fontSize: '0.75rem',
                            '&:hover': { textDecoration: 'underline' }
                        }}
                    >
                        Hai dimenticato la password?
                    </Typography>
                </Box>

                <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    sx={{
                        backgroundColor: '#0b3d87',
                        color: '#fff',
                        borderRadius: '24px',
                        py: 1.2,
                        fontWeight: 'bold',
                        width: "70%",
                        alignSelf: "center",
                        mt: 1,
                        '&:hover': {
                            backgroundColor: '#072b66',
                        }
                    }}
                >
                    {loading ? "Accesso in corso..." : "ACCEDI"}
                </Button>
            </Box>
        </AuthLayout>
    );
}

export default Login; 